/**
 * The supernova: the burst that ends the star. It throws out the shell, the
 * city lights that fly out from the star, each to its own place on the globe,
 * and the ejecta, debris that leaves for good. The blast is two things:
 *
 *   - a shell: every light of a night-side Earth flies straight out from the
 *     star to its own place on the globe and stops there, like a balloon
 *     inflating and setting; nothing turns back, nothing wanders;
 *   - ejecta: debris that leaves for good, in a sphere and a thin tilted disc,
 *     behind a shock front of each, fading as it goes.
 *
 * Each particle's path (straight out to its place, s(u) = 1 − (1 − u)^EXPAND_POW,
 * with a sideways bend that is zero at both ends; the ejecta straight out,
 * slowing) is closed-form and computed on the graphics card from the data
 * seeded here.
 */
import { cities } from '../data/cities'
import { clamp, smoothstep } from '../easing'
import { glow } from '../draw'
import { createRandom } from '../random'
import type { Canvas2D, Particles } from '../renderer/renderer'
import type { Viewport } from '../viewport'

const RAD = Math.PI / 180

/* =====================================================================
   TWEAKABLES. Times are seconds; lengths are in units of min(width, height).
   ===================================================================== */
export const T_DET = 3.87 /* the supernova (a short, dark beat after the implosion) */
const FLASH_A = 0.55 /* the white flash at the supernova (0 = none) */
const SHOCK_A = 0.16 /* the shock fronts: brightness (0 = none) */
const SHOCK_R = 1.15 /* ...how far they run, */
const SHOCK_K = 2.2 /* ...how fast they slow */
const SHOCK_LIFE = 0.55 /* ...how long they glow */

/* the balloon: every light flies straight out to its place and stops */
const EXPAND = 1.9 /* how long the shell takes to reach the globe (average, s) */
const EXPAND_VAR = 0.45 /* ± spread of that per particle: the shell's thickness on the way */
export const EXPAND_POW = 4 /* how explosive the start is (higher = faster out, longer easing in) */
const EXPAND_DELAY = 0.12 /* the last particles leave this long after the burst */
const WOBBLE = 0.07 /* how far a path bends off a straight line on the way (0 = dead straight) */

/* the ejecta: debris that leaves for good */
const EJECTA_DESKTOP = 1500 /* how many */
const EJECTA_MOBILE = 700
const EJECTA_REACH: [number, number] = [0.55, 1.9] /* how far they get: nearest, furthest */
const EJECTA_DRAG = 2.4 /* how fast they slow */
const EJECTA_LIFE: [number, number] = [0.5, 1.7] /* how long they glow */
const RING_SHARE = 0.45 /* share thrown into a thin disc (0 = all a sphere) */
const RING_OPEN = 16 /* how open the disc is seen (deg): 0 = edge-on line, 90 = full circle */

/* the look */
const LIGHTS_DESKTOP = 4600 /* particle count */
const LIGHTS_MOBILE = 2200
const TRAIL_T = [0, 0.7, 1.8] /* trail length over time (s after the burst): start, middle, end */
const TRAIL_V = [
  0.035, 0.025, 0.0,
] /* ...in seconds of the particle's own path: short, so they read as sparks, not comets */
const SEED = 20261008
/* ===================================================================== */

/** When the last light has set, `tau` seconds after the burst. */
export const SHELL_SETTLED = EXPAND * (1 + EXPAND_VAR) + EXPAND_DELAY

/** How far the shell has got, 0..1 (its average, for the dark sphere inflating with it). */
export function shellAt(tau: number): number {
  const u = clamp(tau / EXPAND, 0, 1)
  return 1 - Math.pow(1 - u, EXPAND_POW)
}

/** How long a particle's trail is, in seconds of its own path. */
export function trailAt(tau: number): number {
  if (tau <= TRAIL_T[0]) return TRAIL_V[0]
  if (tau >= TRAIL_T[2]) return TRAIL_V[2]
  const k = tau < TRAIL_T[1] ? 0 : 1
  const u = (tau - TRAIL_T[k]) / (TRAIL_T[k + 1] - TRAIL_T[k])
  return TRAIL_V[k] + (TRAIL_V[k + 1] - TRAIL_V[k]) * u * u * (3 - 2 * u)
}

/**
 * Seeds the shell and the ejecta. The same seed always gives the same
 * particles, so every frame at a given time is the same. `small` is for
 * phones: fewer of each.
 */
export function seedParticles(small: boolean): Particles {
  const random = createRandom(SEED)
  const rnd = random.next
  const gauss = random.gauss
  const lights = small ? LIGHTS_MOBILE : LIGHTS_DESKTOP
  const ejecta = small ? EJECTA_MOBILE : EJECTA_DESKTOP
  const total = lights + ejecta
  const count = cities.count

  const shell = {
    count: lights,
    x: new Float32Array(lights),
    y: new Float32Array(lights),
    z: new Float32Array(lights),
    brightness: new Float32Array(lights),
    bendX: new Float32Array(lights),
    bendY: new Float32Array(lights),
    bendZ: new Float32Array(lights),
    departure: new Float32Array(lights),
    flight: new Float32Array(lights),
  }
  const debris = {
    count: ejecta,
    x: new Float32Array(ejecta),
    y: new Float32Array(ejecta),
    z: new Float32Array(ejecta),
    reach: new Float32Array(ejecta),
    drag: new Float32Array(ejecta),
    life: new Float32Array(ejecta),
  }
  const width = new Float32Array(total)
  const temperature = new Float32Array(total)
  const twinkle = new Float32Array(total)
  const phase = new Float32Array(total)
  const glint = new Float32Array(total)

  /* lights: cities sampled by population^0.55, so megacities become dense
     clusters and small towns still get the odd particle */
  const weights = new Float64Array(count)
  let weightSum = 0
  for (let i = 0; i < count; i++) {
    weights[i] = Math.pow(cities.population[i], 0.55)
    weightSum += weights[i]
  }
  const step = weightSum / lights
  const offset = rnd() * step
  let c = 0
  let run = weights[0]
  for (let k = 0; k < lights; k++) {
    const want = offset + k * step
    while (run < want && c < count - 1) {
      c++
      run += weights[c]
    }
    const population = cities.population[c]
    const sigma = (0.0025 + 0.011 * Math.pow(population / 1e7, 0.35)) * Math.abs(gauss()) * 1.3
    const theta = rnd() * 2 * Math.PI
    const east = Math.cos(theta) * sigma
    const north = Math.sin(theta) * sigma
    const lat = cities.lat[c]
    const lon = cities.lon[c]
    const sinLat = Math.sin(lat)
    const cosLat = Math.cos(lat)
    const sinLon = Math.sin(lon)
    const cosLon = Math.cos(lon)
    const x = cities.x[c] + east * -sinLon + north * -sinLat * cosLon
    const y = cities.y[c] + east * cosLon + north * -sinLat * sinLon
    const z = cities.z[c] + north * cosLat
    const length = Math.sqrt(x * x + y * y + z * z)
    shell.x[k] = x / length
    shell.y[k] = y / length
    shell.z[k] = z / length
    shell.brightness[k] = clamp(
      0.45 + (0.12 * Math.log(population / 1e5)) / Math.LN10 + 0.25 * (1 - sigma / 0.03),
      0.3,
      1
    )
    /* a sideways bend, square to its place */
    let rx = gauss()
    let ry = gauss()
    let rz = gauss()
    const along = rx * shell.x[k] + ry * shell.y[k] + rz * shell.z[k]
    rx -= along * shell.x[k]
    ry -= along * shell.y[k]
    rz -= along * shell.z[k]
    const norm = Math.sqrt(rx * rx + ry * ry + rz * rz) || 1
    const bend = (WOBBLE * (0.3 + 0.7 * rnd())) / norm
    shell.bendX[k] = rx * bend
    shell.bendY[k] = ry * bend
    shell.bendZ[k] = rz * bend
    shell.departure[k] = EXPAND_DELAY * rnd()
    shell.flight[k] = EXPAND * (1 + EXPAND_VAR * (rnd() * 2 - 1))
  }

  /* ejecta */
  const open = RING_OPEN * RAD
  const discY = Math.sin(open)
  const discZ = -Math.cos(open)
  for (let k = 0; k < ejecta; k++) {
    let dx: number
    let dy: number
    let dz: number
    if (rnd() < RING_SHARE) {
      const angle = rnd() * 2 * Math.PI
      const thick = gauss() * 0.05
      dx = Math.cos(angle)
      dy = Math.sin(angle) * discY + thick
      dz = Math.sin(angle) * discZ
    } else {
      dz = rnd() * 2 - 1
      const angle = rnd() * 2 * Math.PI
      const ring = Math.sqrt(1 - dz * dz)
      dx = Math.cos(angle) * ring
      dy = Math.sin(angle) * ring
    }
    const length = Math.sqrt(dx * dx + dy * dy + dz * dz)
    debris.x[k] = dx / length
    debris.y[k] = dy / length
    debris.z[k] = dz / length
    debris.reach[k] = EJECTA_REACH[0] + (EJECTA_REACH[1] - EJECTA_REACH[0]) * Math.pow(rnd(), 1.5)
    debris.drag[k] = EJECTA_DRAG * (0.7 + 0.6 * rnd())
    debris.life[k] = EJECTA_LIFE[0] + (EJECTA_LIFE[1] - EJECTA_LIFE[0]) * rnd()
  }
  for (let k = 0; k < total; k++) {
    width[k] = 0.55 + Math.pow(rnd(), 2.2) * 1.4
    temperature[k] = rnd()
    twinkle[k] = 4 + rnd() * 10
    phase[k] = rnd() * 6.283
    glint[k] = 0.3 + rnd() * 0.8
  }
  return { shell, ejecta: debris, width, temperature, twinkle, phase, glint }
}

/** The flash, and the shock fronts of the sphere and of the disc, `tau` seconds after the burst. */
export function drawBlast(ctx: Canvas2D, vp: Viewport, tau: number) {
  /* the flash */
  const flash = Math.exp(-tau * 7) * smoothstep(0, 0.03, tau)
  glow(ctx, vp.cx, vp.cy, vp.min * (0.08 + 0.7 * (1 - Math.exp(-tau * 6))), FLASH_A * flash, 0.22)
  glow(ctx, vp.cx, vp.cy, vp.min * 0.03, 1.2 * Math.exp(-tau * 4) * smoothstep(0, 0.02, tau), 0.3)
  /* shock fronts: a sphere (a circle) and the disc (a thin ellipse) */
  const alpha = SHOCK_A * Math.exp(-tau / SHOCK_LIFE) * smoothstep(0, 0.04, tau)
  if (alpha <= 0.004) return
  const radius = vp.min * SHOCK_R * (1 - Math.exp(-tau * SHOCK_K))
  for (let front = 0; front < 2; front++) {
    const squash = front ? Math.max(Math.sin(RING_OPEN * RAD), 0.05) : 1
    const a = front ? alpha * 0.9 * (RING_SHARE > 0 ? 1 : 0) : alpha
    if (a < 0.004) continue
    ctx.save()
    ctx.translate(vp.cx, vp.cy)
    ctx.scale(1, squash)
    const gradient = ctx.createRadialGradient(0, 0, radius * 0.55, 0, 0, radius * 1.04)
    gradient.addColorStop(0, 'rgba(242,215,174,0)')
    gradient.addColorStop(0.7, 'rgba(250,232,200,' + (a * 0.35).toFixed(3) + ')')
    gradient.addColorStop(0.9, 'rgba(255,250,240,' + a.toFixed(3) + ')')
    gradient.addColorStop(1, 'rgba(255,250,240,0)')
    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.arc(0, 0, radius * 1.02, 0, 6.2832)
    ctx.fill()
    ctx.restore()
  }
}
